import MenuIcon from '@mui/icons-material/Menu';
import {
  AppBar,
  Box,
  Divider,
  Drawer,
  IconButton,
  useTheme,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { NavLocation } from '@schamburg-web/interfaces';
import React, { useState } from 'react';
import NavLink from '../NavLink/NavLink';
import SearchBox from '../SearchBox/SearchBox';
import ModeSwitcher from '../ModeSwitcher/ModeSwitcher';
import { GoogleShowLocation } from '../GoogleMap';
import { Link, useLocation } from 'react-router-dom';

const HeaderBar: React.FC<{
  logoClass: string;
  className: string;
  appTitle?: string;
  navLocations?: NavLocation[];
  showLocation?: boolean;
  onLocationToggle?: (enabled: boolean) => void;
  children?: React.ReactNode;
  showSearchBox?: boolean;
}> = ({
  logoClass,
  className,
  navLocations,
  showLocation,
  onLocationToggle,
  children,
  showSearchBox = true,
}) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('md'));

  if (matches)
    return (
      <DesktopHeaderBar
        logoClass={logoClass}
        className={className}
        navLocations={navLocations}
        showSearchBox={showSearchBox}
        showLocation={showLocation}
        onLocationToggle={onLocationToggle}
      >
        {children}
      </DesktopHeaderBar>
    );
  else
    return (
      <MobileHeaderBar
        logoClass={logoClass}
        className={className}
        navLocations={navLocations}
        showSearchBox={showSearchBox}
        showLocation={showLocation}
        onLocationToggle={onLocationToggle}
      >
        {children}
      </MobileHeaderBar>
    );
};

const DesktopHeaderBar: React.FC<{
  logoClass: string;
  className: string;
  appTitle?: string;
  navLocations?: NavLocation[];
  showLocation?: boolean;
  onLocationToggle?: (enabled: boolean) => void;
  children?: React.ReactNode;
  showSearchBox?: boolean;
}> = ({
  logoClass,
  className,
  appTitle,
  navLocations,
  showLocation,
  onLocationToggle,
  children,
  showSearchBox = true,
}) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('md'));

  if (!matches) return null;

  return (
    <AppBar
      position="static"
      elevation={0}
      className={className}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          minWidth: 250,
          maxWidth: 250,
          height: '100%',
        }}
      >
        <Link className={logoClass} to="/" title="Return to Main Map" />
      </Box>
      <Box
        component="nav"
        role="navigation"
        aria-label="Primary navigation"
        sx={{
          display: 'flex',
          textAlign: 'left',
          flexDirection: 'row',
          alignItems: 'center',
          marginRight: 1,
          marginLeft: 1,
          flexGrow: 1,
          height: '100%',
        }}
      >
        {navLocations && <NavLinks navLocations={navLocations} />}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: 1,
          height: '100%',
        }}
      >
        {children}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          height: '100%',
          marginRight: 1,
        }}
      >
        {showSearchBox && <SearchBox />}
        {children && showLocation && (
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              mx: 0.5,
              backgroundColor: 'currentColor',
              opacity: 0.3,
            }}
          />
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginRight: 1.25,
          alignItems: 'center',
          height: '100%',
          gap: 0.5,
        }}
      >
        {showLocation && <GoogleShowLocation color="inherit" size="medium" />}
        <ModeSwitcher />
      </Box>
    </AppBar>
  );
};

const drawerWidth = 350;

const MobileHeaderBar: React.FC<{
  logoClass: string;
  className: string;
  secondaryLogoClass?: string;
  appTitle?: string;
  navLocations?: NavLocation[];
  showLocation?: boolean;
  onLocationToggle?: (enabled: boolean) => void;
  children?: React.ReactNode;
  showSearchBox?: boolean;
}> = ({
  logoClass,
  className,
  secondaryLogoClass,
  appTitle,
  navLocations,
  showLocation,
  onLocationToggle,
  children,
  showSearchBox = true,
}) => {
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const matches = useMediaQuery(theme.breakpoints.up('md'));

  if (matches) return null;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      className={className}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          height: '100%',
        }}
        component={Link}
        {...{ to: '/' }}
      >
        <Box className={logoClass} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginRight: 1.25,
          alignItems: 'center',
          height: '100%',
          gap: 0.5,
        }}
      >
        {showLocation && <GoogleShowLocation color="inherit" size="medium" />}
        <ModeSwitcher />
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginRight: 1.25,
          alignItems: 'center',
          height: '100%',
        }}
      >
        <IconButton
          color="inherit"
          aria-label="open or close sidebar"
          edge="start"
          onClick={handleDrawerToggle}
          title="Open or Close sidebar"
        >
          <MenuIcon />
        </IconButton>
      </Box>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        anchor="right"
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        className="drawer"
        sx={{
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: '85%',
            maxWidth: drawerWidth,
          },
        }}
      >
        {showSearchBox && (
          <>
            <Box
              sx={{
                display: 'flex',
                flex: 1,
                minWidth: 275,
                maxWidth: '100%',
                pt: 2,
                maxHeight: 55,
                textAlign: 'center',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <SearchBox />
            </Box>
            <Divider sx={{ padding: 1 }} />
          </>
        )}

        {navLocations && <NavLinks navLocations={navLocations} />}
        {children && <Divider sx={{ padding: 1 }} />}
        {children && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              py: 2,
            }}
          >
            {children}
          </Box>
        )}
      </Drawer>
    </AppBar>
  );
};

const NavLinks: React.FC<{ navLocations: NavLocation[] }> = ({
  navLocations,
}) => {
  const locationHook = useLocation();
  const currentPath = locationHook.pathname;
  return navLocations
    .filter(
      (navLocation) =>
        navLocation.showOnSelf !== false || currentPath !== navLocation.link
    )
    .map((navLocation) => (
      <NavLink key={navLocation.name} location={navLocation} />
    ));
};

export default HeaderBar;
